import { z } from "zod";
import * as Sentry from "@sentry/react";

/**
 * The Amplitude project `GET /user/` named for this session (MS-3413). Values
 * match `AmplitudeDestination` in the backend; `WRITE_KEYS` in `lib/metrics`
 * is keyed on the inferred type, so a new project fails to compile until it
 * has a key.
 */
export const AnalyticsDestinationEnum = z.enum(["prod", "qa"]);

export type AnalyticsDestination = z.infer<typeof AnalyticsDestinationEnum>;

/**
 * The `analyticsDestination` field of `GET /user/`, or null for do-not-track.
 *
 * `catch` rather than a hard failure: silently not tracking beats breaking
 * session hydration over telemetry. The miss is still reported, because an
 * absent field or an unrecognised name is a contract mismatch with the backend
 * and would otherwise be indistinguishable from a deliberate null.
 */
export const AnalyticsDestinationSchema =
  AnalyticsDestinationEnum.nullable().catch((ctx) => {
    Sentry.captureException(
      new Error(
        `Unrecognised analyticsDestination ${JSON.stringify(ctx.input)}: ${ctx.error.issues[0]?.code}`,
      ),
      { tags: { source: "metrics", operation: "parseDestination" } },
    );
    return null;
  });

/**
 * Who this session is in Amplitude (MS-3889, MS-3895): the user id to identify
 * by, the `account` group and the derived user properties, all composed by the
 * backend from the same helpers as server events, so browser and server events
 * land on one user carrying the same values.
 *
 * `baseRole` and `workspaceType` each `catch` on their own: an unrecognised
 * value costs only that property, never the user id beside it.
 */
export const AnalyticsIdentitySchema = z.object({
  userId: z.string(),
  accountId: z.string().nullable(),
  baseRole: z
    .enum(["admin", "member", "viewer", "custom"])
    .nullable()
    .catch(null),
  workspaceType: z.enum(["partner", "standard"]).nullable().catch(null),
});

export type AnalyticsIdentity = z.infer<typeof AnalyticsIdentitySchema>;

/**
 * The `analytics*` identity fields of `GET /user/`, or null when the user id is
 * absent. Not reported like a bad destination: a backend predating these
 * fields is expected during rollout, and null falls back to `user.id`.
 */
export function parseAnalyticsIdentity(response: {
  analyticsUserId?: unknown;
  analyticsAccountId?: unknown;
  analyticsBaseRole?: unknown;
  analyticsWorkspaceType?: unknown;
}): AnalyticsIdentity | null {
  const parsed = AnalyticsIdentitySchema.safeParse({
    userId: response.analyticsUserId,
    accountId: response.analyticsAccountId ?? null,
    baseRole: response.analyticsBaseRole ?? null,
    workspaceType: response.analyticsWorkspaceType ?? null,
  });
  return parsed.success ? parsed.data : null;
}
